<template>
  <div v-if="summary" class="voyage-summary-section">
    <h4 class="section-title text-primary font-weight-bold mb-2 pb-2 border-bottom">Voyage Summary</h4>

    <!-- Combined Navigation & Main Engine Metrics -->
    <div class="row mb-2">
      <div class="col-12">
        <div class="summary-section mb-0 iq-card">
          <div class="section-header combined-metrics-header">
            <i class="ri-compass-3-line section-icon"></i>
            <span class="section-title-text">Navigation & Main Engine Metrics</span>
          </div>
          <div class="section-content">
            <!-- Navigation Metrics Row -->
            <div class="row mb-2">
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">Voyage Duration</h6>
                  </div>
                  <div class="metric-content">
                    <div class="ae-metrics-grid">
                      <div class="ae-metric-item">
                        <i class="ri-time-line fs-3 text-secondary me-2"></i>
                        <div class="ae-metric-content">
                          <span class="ae-metric-label">Duration</span>
                          <span class="ae-metric-value">
                            {{ formatVoyageDuration(voyage?.mvygStartTime, voyage?.mvygEndTime) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">Total Distance</h6>
                  </div>
                  <div class="metric-content">
                    <div class="ae-metrics-grid">
                      <div class="ae-metric-item">
                        <i class="ri-route-line fs-3 text-secondary me-2"></i>
                        <div class="ae-metric-content">
                          <span class="ae-metric-label">Distance</span>
                          <span class="ae-metric-value">
                            {{ formatDistanceNM(summary.navigationMetrics?.totalDistance) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">Average Speed</h6>
                  </div>
                  <div class="metric-content">
                    <div class="ae-metrics-grid">
                      <div class="ae-metric-item">
                        <i class="ri-speed-line fs-3 text-secondary me-2"></i>
                        <div class="ae-metric-content">
                          <span class="ae-metric-label">Speed</span>
                          <span class="ae-metric-value">
                            {{ formatNumber(summary.navigationMetrics?.averageSpeed / 1.852) }} kn
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Engine Metrics - Cards Section -->
            <div class="row">
              <!-- PORT Engine RPM -->
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">PORT ENGINE - RPM</h6>
                  </div>
                  <div class="metric-content">
                    <div class="radial-gauge-wrapper">
                      <RadialGaugeChart
                        :value="summary.mainEngineMetrics?.averageRpm?.PORT"
                        :max="1600"
                        label="RPM"
                        color="#0084ff"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- STBD Engine RPM -->
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">STBD ENGINE - RPM</h6>
                  </div>
                  <div class="metric-content">
                    <div class="radial-gauge-wrapper">
                      <RadialGaugeChart
                        :value="summary.mainEngineMetrics?.averageRpm?.STARBOARD"
                        :max="1600"
                        label="RPM"
                        color="#0084ff"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Total Summary -->
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">TOTAL SUMMARY</h6>
                  </div>
                  <div class="metric-content">
                    <div class="ae-metrics-grid">
                      <div class="ae-metric-item">
                        <span class="ae-metric-label">Total Engine Cons</span>
                        <span class="ae-metric-value">
                          {{ formatNumber(summary.mainEngineMetrics?.totalEngineCons) }} L
                        </span>
                      </div>
                      <div class="ae-metric-item">
                        <span class="ae-metric-label">Total Running Time</span>
                        <span class="ae-metric-value">
                          {{ formatTotalRunningTime }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fuel Consumption & Running Time Charts -->
            <div class="row mt-2">
              <!-- Fuel Consumption Chart -->
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">Fuel Consumption Comparison</h6>
                  </div>
                  <div class="metric-content">
                    <DonutChart :data="engineFuelChartData" label-key="name" value-key="value"
                      :colors="['#fd7e14', '#0dcaf0']" />
                  </div>
                </div>
              </div>

              <!-- Running Time Chart -->
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">Running Time Comparison</h6>
                  </div>
                  <div class="metric-content">
                    <DonutChart :data="engineRunningChartData" label-key="name" value-key="value"
                      :colors="['#20c997', '#6f42c1']" />
                  </div>
                </div>
              </div>

              <!-- Detailed Metrics Table -->
              <div class="col-md-4">
                <div class="engine-metric-card iq-card-inner p-2 w-100 h-100">
                  <div class="metric-header">
                    <h6 class="text-secondary text-uppercase font-weight-bold small mb-0">Detailed Metrics</h6>
                  </div>
                  <div class="metric-content">
                    <div class="detailed-metrics-grid">
                      <div class="detailed-metric-card">
                        <h6 class="text-secondary text-uppercase font-weight-bold small mb-2 text-center">PORT ENGINE</h6>
                        <div class="detailed-metric-item">
                          <span class="detailed-metric-label">Fuel Consumption</span>
                          <span class="detailed-metric-value">
                            {{ formatNumber(summary.mainEngineMetrics?.fuelConsEngine?.port || 0) }} L
                          </span>
                        </div>
                        <div class="detailed-metric-item">
                          <span class="detailed-metric-label">Running Time</span>
                          <span class="detailed-metric-value">
                            {{ formatDuration(summary.mainEngineMetrics?.totalRunningSeconds?.PORT || 0) }}
                          </span>
                        </div>
                      </div>
                      <div class="detailed-metric-card">
                        <h6 class="text-secondary text-uppercase font-weight-bold small mb-2 text-center">STBD ENGINE</h6>
                        <div class="detailed-metric-item">
                          <span class="detailed-metric-label">Fuel Consumption</span>
                          <span class="detailed-metric-value">
                            {{ formatNumber(summary.mainEngineMetrics?.fuelConsEngine?.starboard || 0) }} L
                          </span>
                        </div>
                        <div class="detailed-metric-item">
                          <span class="detailed-metric-label">Running Time</span>
                          <span class="detailed-metric-value">
                            {{ formatDuration(summary.mainEngineMetrics?.totalRunningSeconds?.STARBOARD || 0) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Auxiliary Engine Metrics - Combined Card -->
    <div class="row mb-2">
      <div class="col-12">
        <div class="summary-section mb-0 iq-card">
          <div class="section-header auxiliary-combined-header">
            <i class="ri-engine-line section-icon"></i>
            <span class="section-title-text">Auxiliary Engine Metrics</span>
          </div>
          <div class="section-content">
            <div class="row">
              <!-- Running Hours Chart -->
              <div class="col-md-3">
                <h6 class="text-secondary text-uppercase font-weight-bold small mb-2 text-center">Running Hours</h6>
                <DonutChart :data="aeRunningChartData" label-key="name" value-key="runningSeconds"
                  :colors="['#fd7e14', '#20c997', '#0dcaf0']" />
              </div>

              <!-- Fuel Consumption Chart -->
              <div class="col-md-3">
                <h6 class="text-secondary text-uppercase font-weight-bold small mb-2 text-center">Fuel Consumption</h6>
                <DonutChart :data="aeFuelChartData" label-key="name" value-key="fuel"
                  :colors="['#ffc107', '#ff9800', '#ff5722']" />
              </div>

              <!-- Combined Table -->
              <div class="col-md-6">
                <div class="table-responsive">
                  <b-table :items="aeCombinedItems" :fields="aeCombinedFields" striped hover responsive
                    class="text-center mb-0" head-variant="light" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Weather Metrics Card -->
    <div class="row mb-2">
      <div class="col-12">
        <div class="summary-section mb-0 iq-card">
          <div class="section-header weather-header">
            <i class="ri-cloudy-line section-icon"></i>
            <span class="section-title-text">Weather Metrics</span>
          </div>
          <div class="section-content">
            <div class="summary-grid row mb-0">
              <!-- Average Wave Height -->
              <div class="col-4 mb-2">
                <div class="summary-item iq-card-inner p-2 w-100 h-100">
                  <div class="d-flex flex-column">
                    <span class="summary-label text-secondary text-uppercase font-weight-bold small mb-1">Average Wave
                      Height</span>
                    <span class="summary-value text-dark">{{ formatNumber(summary.weatherMetrics?.averageWaveHeight) }}
                      <small class="text-secondary">meters</small></span>
                  </div>
                </div>
              </div>

              <!-- Sea State -->
              <div class="col-4 mb-2">
                <div class="summary-item iq-card-inner p-2 w-100 h-100">
                  <div class="d-flex flex-column">
                    <span class="summary-label text-secondary text-uppercase font-weight-bold small mb-1">Sea
                      State</span>
                    <span :class="getSeaStateTextClass(summary.weatherMetrics?.averageWaveHeightString)"
                      class="summary-value font-weight-bold">
                      {{ summary.weatherMetrics?.averageWaveHeightString || 'N/A' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Average Wave Period -->
              <div class="col-4 mb-2">
                <div class="summary-item iq-card-inner p-2 w-100 h-100">
                  <div class="d-flex flex-column">
                    <span class="summary-label text-secondary text-uppercase font-weight-bold small mb-1">Average Wave
                      Period</span>
                    <span class="summary-value text-dark">{{ formatNumber(summary.weatherMetrics?.averageWavePeriod) }}
                      <small class="text-secondary">seconds</small></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Wave Direction Analysis -->
    <div class="row mb-2">
      <div class="col-12">
        <div class="summary-section mb-0 iq-card">
          <div class="section-header wave-direction-header">
            <i class="ri-compass-3-line section-icon"></i>
            <span class="section-title-text">Wave Direction Analysis</span>
          </div>
          <div class="section-content">
            <!-- Average Speed Row (12 col) -->
            <div class="row mb-2">
              <div class="col-12">
                <h6 class="text-secondary text-uppercase font-weight-bold small mb-2">Average Speed</h6>
                <div class="summary-grid row mb-0">
                  <div class="col-4 mb-2">
                    <div class="summary-item iq-card-inner p-2 w-100 h-100">
                      <div class="d-flex flex-column">
                        <span class="summary-label text-secondary text-uppercase font-weight-bold small mb-1">Head
                          Seas</span>
                        <span class="summary-value text-dark">{{
                          formatNumber(summary.weatherMetrics?.averageSpeedDirection?.headSeas / 1.852) }} <small
                            class="text-secondary">knots</small></span>
                      </div>
                    </div>
                  </div>
                  <div class="col-4 mb-2">
                    <div class="summary-item iq-card-inner p-2 w-100 h-100">
                      <div class="d-flex flex-column">
                        <span class="summary-label text-secondary text-uppercase font-weight-bold small mb-1">Following
                          Seas</span>
                        <span class="summary-value text-dark">{{
                          formatNumber(summary.weatherMetrics?.averageSpeedDirection?.followingSeas / 1.852) }} <small
                            class="text-secondary">knots</small></span>
                      </div>
                    </div>
                  </div>
                  <div class="col-4 mb-2">
                    <div class="summary-item iq-card-inner p-2 w-100 h-100">
                      <div class="d-flex flex-column">
                        <span class="summary-label text-secondary text-uppercase font-weight-bold small mb-1">Beam
                          Seas</span>
                        <span class="summary-value text-dark">{{
                          formatNumber(summary.weatherMetrics?.averageSpeedDirection?.beamSeas / 1.852) }} <small
                            class="text-secondary">knots</small></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Duration Row (12 col) -->
            <div class="row">
              <div class="col-12">
                <h6 class="text-secondary text-uppercase font-weight-bold small mb-2">Duration</h6>
                <div class="row align-items-center">
                  <div class="col-5 col-lg-4">
                    <DonutChart :data="waveDurationChartData" label-key="direction" value-key="runningSeconds"
                      :colors="['#6f42c1', '#691b7b', '#7952b3']" />
                  </div>
                  <div class="col-7 col-lg-8">
                    <b-table :items="waveDurationItems" :fields="waveDurationFields" striped hover responsive
                      class="text-center mb-0" head-variant="light" small />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Operation Mode Metrics -->
    <div v-if="operationModeMetricsDebug?.engine" class="summary-section mb-2 iq-card">
      <div class="section-header operation-header">
        <i class="ri-settings-4-line section-icon"></i>
        <span class="section-title-text">Operation Mode Metrics</span>
      </div>
      <div class="section-content">
        <div v-for="(engineData, engineType) in operationModeMetricsDebug.engine" :key="engineType" class="mb-2">
          <h6 class="text-secondary text-uppercase font-weight-bold small mb-2">{{ engineType }}</h6>
          <div class="row align-items-center">
            <div class="col-md-4 col-lg-3">
              <DonutChart :data="getOperationModeChartData(engineData)"
                :colors="['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7']" />
            </div>
            <div class="col-md-8 col-lg-9">
              <b-table :items="getOperationModeItems(engineData)" :fields="operationModeFields" striped hover responsive
                class="text-center mb-0" head-variant="light" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVoyageFormatters } from '@/views/voyage/composables/useVoyageFormatters';
import {
  getOperationModeItems as getOpModeItems,
  getAeCombinedItems,
  getWaveDurationItems,
} from '@/views/voyage/utils/chartExtractors';
import DonutChart from './charts/DonutChart.vue';
import RadialGaugeChart from './charts/RadialGaugeChart.vue';

export default {
  name: 'VoyageSummary',
  components: {
    DonutChart,
    RadialGaugeChart,
  },
  props: {
    voyage: {
      type: Object,
      default: null,
    },
    summary: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const {
      formatNumber,
      formatDistanceNM,
      formatDuration,
      formatVoyageDuration,
      getSeaStateTextClass,
    } = useVoyageFormatters();

    return {
      formatNumber,
      formatDistanceNM,
      formatDuration,
      formatVoyageDuration,
      getSeaStateTextClass,
    };
  },
  data() {
    return {
      operationModeFields: [
        { key: 'modeName', label: 'Mode', sortable: true },
        { key: 'percentage', label: 'Percentage', sortable: true },
        { key: 'runningTime', label: 'Running Time', sortable: true },
      ],
      aeCombinedFields: [
        { key: 'engineName', label: 'Engine', sortable: true },
        { key: 'runningTime', label: 'Running Hours', sortable: true },
        { key: 'runningPercentage', label: '% Running', sortable: true },
        { key: 'fuelConsumption', label: 'Fuel Consumption', sortable: true },
        { key: 'fuelPercentage', label: '% Fuel', sortable: true },
      ],
      waveDurationFields: [
        { key: 'directionName', label: 'Direction', sortable: true },
        { key: 'percentage', label: 'Percentage', sortable: true },
        { key: 'duration', label: 'Duration', sortable: true },
      ],
    };
  },
  computed: {
    aeCombinedItems() {
      return getAeCombinedItems(this.summary?.aeMetrics);
    },
    waveDurationItems() {
      return getWaveDurationItems(this.summary?.weatherMetrics);
    },
    aeRunningChartData() {
      if (!this.summary?.aeMetrics?.runningSeconds) return [];
      return Object.entries(this.summary.aeMetrics.runningSeconds)
        .map(([engineName, seconds]) => ({
          name: engineName,
          runningSeconds: seconds || 0,
        }))
        .filter((d) => d.runningSeconds > 0);
    },
    aeFuelChartData() {
      if (!this.summary?.aeMetrics?.fuelConsumption) return [];
      return Object.entries(this.summary.aeMetrics.fuelConsumption)
        .map(([engineName, fuel]) => ({
          name: engineName,
          fuel: fuel || 0,
        }))
        .filter((d) => d.fuel > 0);
    },
    waveDurationChartData() {
      if (!this.summary?.weatherMetrics?.runningSecondsDirection) return [];
      return Object.entries(this.summary.weatherMetrics.runningSecondsDirection)
        .map(([direction, seconds]) => ({
          direction: direction.replace(/([A-Z])/g, ' $1').trim(),
          runningSeconds: seconds || 0,
        }))
        .filter((d) => d.runningSeconds > 0);
    },
    operationModeMetricsDebug() {
      console.log('[VoyageSummary] operationModeMetrics:', JSON.parse(JSON.stringify(this.summary?.operationModeMetrics)));
      return this.summary?.operationModeMetrics;
    },
    engineFuelChartData() {
      const port = this.summary?.mainEngineMetrics?.fuelConsEngine?.port || 0;
      const starboard = this.summary?.mainEngineMetrics?.fuelConsEngine?.starboard || 0;
      return [
        { name: 'PORT', value: port },
        { name: 'STBD', value: starboard },
      ];
    },
    engineRunningChartData() {
      const port = this.summary?.mainEngineMetrics?.totalRunningSeconds?.PORT || 0;
      const starboard = this.summary?.mainEngineMetrics?.totalRunningSeconds?.STARBOARD || 0;
      return [
        { name: 'PORT', value: port },
        { name: 'STBD', value: starboard },
      ];
    },
    formatTotalRunningTime() {
      const port = this.summary?.mainEngineMetrics?.totalRunningSeconds?.PORT || 0;
      const starboard = this.summary?.mainEngineMetrics?.totalRunningSeconds?.STARBOARD || 0;
      const totalSeconds = port + starboard;
      return this.formatDuration(totalSeconds);
    },
  },
  methods: {
    getOperationModeItems(engineData) {
      return getOpModeItems(engineData);
    },
    getOperationModeChartData(engineData) {
      if (!engineData) {
        console.warn('[VoyageSummary] getOperationModeChartData: engineData is null/undefined');
        return [];
      }
      const chartData = Object.entries(engineData)
        .map(([modeName, data]) => ({
          name: modeName,
          value: data.runningSeconds || 0,
        }))
        .filter((d) => d.value > 0);

      console.log('[VoyageSummary] getOperationModeChartData:', {
        engineData: JSON.parse(JSON.stringify(engineData)),
        chartData: JSON.parse(JSON.stringify(chartData)),
      });

      return chartData;
    },
  },
};
</script>

<style scoped>
.voyage-summary-section {
  margin-top: 0.75rem;
}

.summary-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: box-shadow 0.2s ease;
}

.summary-section:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* IQ Card styles */
.summary-section.iq-card {
  background: #ffffff;
  border: 2px solid #E8ECF3;
  border-radius: 20px;
  box-shadow: none;
}

.summary-section.iq-card:hover {
  box-shadow: none;
}

.section-header {
  display: flex;
  align-items: center;
  padding: 0.4rem 0.6rem;
  border-bottom: 2px solid;
  gap: 0.4rem;
}

.section-icon {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.section-title-text {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-content {
  padding: 0.5rem;
}

/* Color-coded section headers */
.navigation-header {
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.05) 0%, rgba(13, 110, 253, 0.1) 100%);
  border-color: #0d6efd;
  color: #0d6efd;
}

.engine-header {
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.1) 100%);
  border-color: #dc3545;
  color: #dc3545;
}

.combined-metrics-header {
  background: linear-gradient(135deg, rgba(108, 92, 231, 0.05) 0%, rgba(108, 92, 231, 0.1) 100%);
  border-color: #6c5ce7;
  color: #6c5ce7;
}

.auxiliary-header {
  background: linear-gradient(135deg, rgba(253, 126, 20, 0.05) 0%, rgba(253, 126, 20, 0.1) 100%);
  border-color: #fd7e14;
  color: #fd7e14;
}

.auxiliary-combined-header {
  background: linear-gradient(135deg, rgba(253, 126, 20, 0.05) 0%, rgba(253, 126, 20, 0.1) 100%);
  border-color: #fd7e14;
  color: #fd7e14;
}

.fuel-header {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 193, 7, 0.1) 100%);
  border-color: #ffc107;
  color: #e0a800;
}

.weather-header {
  background: linear-gradient(135deg, rgba(13, 202, 240, 0.05) 0%, rgba(13, 202, 240, 0.1) 100%);
  border-color: #0dcaf0;
  color: #0dcaf0;
}

.wave-direction-header {
  background: linear-gradient(135deg, rgba(111, 66, 193, 0.05) 0%, rgba(111, 66, 193, 0.1) 100%);
  border-color: #6f42c1;
  color: #6f42c1;
}

.operation-header {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(102, 126, 234, 0.1) 100%);
  border-color: #667eea;
  color: #667eea;
}

/* Engine chart card styles */
.engine-chart-card {
  background: #f8f9fa !important;
  border: 1px solid #E8ECF3 !important;
  border-radius: 12px !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chart-item.mb-2 {
  min-height: 220px;
}

.chart-item:last-child {
  min-height: 200px;
}

.radial-gauge-wrapper {
  width: 100%;
  height: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Engine metric card styles */
.engine-metric-card {
  background: #ffffff !important;
  border: 2px solid #E8ECF3 !important;
  border-radius: 20px !important;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
}

.engine-metric-card:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.25rem 0.75rem rgba(13, 110, 253, 0.1) !important;
  transform: translateY(-1px);
}

.metric-header {
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid #E8ECF3;
  margin: -0.5rem -0.75rem 0.75rem;
}

.metric-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0.5rem;
}

/* Radial gauge wrapper */
.radial-gauge-wrapper {
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Detailed metrics grid */
.detailed-metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  padding: 0.25rem;
  width: 100%;
}

.detailed-metric-card {
  background: #f8f9fa;
  border: 1px solid #E8ECF3;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.detailed-metric-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0;
  width: 100%;
}

.detailed-metric-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.detailed-metric-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0084ff;
  line-height: 1.2;
}

/* AE Metrics grid */
.ae-metrics-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
}

.ae-metric-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #E8ECF3;
}

.ae-metric-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.ae-metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.5px;
}

.ae-metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0084ff;
  line-height: 1.2;
}

/* Donut chart wrapper */
.donut-chart-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 220px;
  width: 100%;
}

/* Chart legend */
.chart-legend {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
}

.legend-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.legend-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
}

.legend-value {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.summary-item-full {
  padding: 0.35rem;
  border: 1px solid #dee2e6 !important;
  background-color: #fff !important;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.summary-item-full:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.25rem 0.75rem rgba(13, 110, 253, 0.15);
  transform: translateY(-1px);
}

/* Card hover effects */
.summary-item,
.summary-item-full {
  padding: 0.35rem;
  border: 1px solid #dee2e6 !important;
  background-color: #fff !important;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.summary-item:hover,
.summary-item-full:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.25rem 0.75rem rgba(13, 110, 253, 0.15);
  transform: translateY(-1px);
}

/* IQ Card Inner */
.summary-item.iq-card-inner,
.summary-item-full.iq-card-inner {
  background: #f8f9fa !important;
  border: 1px solid #E8ECF3 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
}

.summary-item.iq-card-inner:hover,
.summary-item-full.iq-card-inner:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.25rem 0.75rem rgba(13, 110, 253, 0.1) !important;
  transform: translateY(-1px);
}
</style>
